import { Injectable } from '@angular/core';
import { MessageService } from '@sla-ng/core';
import { FieldType, IField, ISelectData } from '@sla-ng/ui-material';
import * as moment from 'moment';
import { BehaviorSubject, finalize, firstValueFrom, Observable, of, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { IAddHourlyWage } from '../../../models/add-hourly-wage-model';
import { ICostAccountingType } from '../../../models/cost-accounting-type.model';
import { ICostCenterGroup } from '../../../models/cost-center-group.model';
import { ICostCenterReferenceValue } from '../../../models/cost-center-reference-value.model';
import { ICostCenter } from '../../../models/cost-center.model';
import { IHourlyWage } from '../../../models/hourly-wage-model';
import { IReference } from '../../../models/reference.model';
import { ICostCenterGroupDetail } from '../../../models/util/kostenstellengruppe-detail.model';
import { CostAccountingTypeResource } from '../../../resources/cost-accounting-type.resource';
import { CostCenterGroupResource } from '../../../resources/cost-center-group.resource';
import { CostCenterResource } from '../../../resources/cost-center.resource';
import { ReferenceResource } from '../../../resources/reference.resource';
import { ICostCenterState } from './cost-center-state.model';

let _state: ICostCenterState = {
    costAccountingTypeLookup: {},
    hourlyWageAddFields: [],
    costAccountingTypes: [],
    hourlyWageFilter: [],
    hourlyWages: [],
    isLoadingHourlyWages: false,
    selectedCostCenter: null,
    costCenterColumns: [ 'shortName', 'name', 'assignments', 'hourlyWages', 'actions' ],
    costCenterGroupColumns: [ 'processOrder', 'name', 'add-button' ],
    hourlyWageColumns: [ 'validFrom', 'validTo', 'costAccountingType', 'hourlyWageCt', 'delete' ],
    isLoadingBezugsgroessen: false,
    references: [],
    referenceLookup: {},
    referencesValuesForCostCenter: [],
    costCenters: [],
    costCenterGroups: [],
    costCenterGroupDetail: [],
    costCenterDialogFields: [],
    costCenterDaterangeField: null,
    isLoading: false,
    costCenterGroupDialogFields: []
};

@Injectable({
    providedIn: 'root',
})
export class CostCenterFacade {

    protected store$: BehaviorSubject<ICostCenterState> = new BehaviorSubject<ICostCenterState>(_state);

    constructor(private myCostCenterGroupResource: CostCenterGroupResource,
                private myCostCenterResource: CostCenterResource,
                private myBezugsgroessenResource: ReferenceResource,
                private myCostAccountingTypeResource: CostAccountingTypeResource,
                private myMessageService: MessageService) {
    }

    public getStateSnapshot(): ICostCenterState {
        return this.store$.value;
    }

    public clearState(): void {
        this.updateState({
            costAccountingTypeLookup: {},
            hourlyWageAddFields: [],
            costAccountingTypes: [],
            hourlyWageFilter: [],
            hourlyWages: [],
            isLoadingHourlyWages: false,
            selectedCostCenter: null,
            costCenterColumns: [ 'shortName', 'name', 'assignments', 'hourlyWages', 'actions' ],
            costCenterGroupColumns: [ 'processOrder', 'name', 'add-button' ],
            hourlyWageColumns: [ 'validFrom', 'validTo', 'costAccountingType', 'hourlyWageCt', 'delete' ],
            isLoadingBezugsgroessen: false,
            references: [],
            referenceLookup: {},
            referencesValuesForCostCenter: [],
            costCenters: [],
            costCenterGroups: [],
            costCenterGroupDetail: [],
            costCenterDialogFields: [],
            isLoading: false,
            costCenterDaterangeField: null,
            costCenterGroupDialogFields: []
        });
    }

    public async fetchData(): Promise<void> {
        this.setIsloading(true);

        const [ , bezugsgroessen ]: [ void, IReference[] ] = await Promise.all([
            this.fetchCostCenters(),
            firstValueFrom(this.myBezugsgroessenResource.getAllReferences()).catch(() => []),
        ]);
        this.updateState({ ..._state, references: bezugsgroessen });
        this.setDialogFilterFields();
        this.setIsloading(false);
        this.createReferenceLookup(bezugsgroessen);
    }

    public async fetchCostCenters(startDate?: moment.Moment, endDate?: moment.Moment): Promise<void> {
        this.setIsloading(true);
        const kostenStellenGruppen: ICostCenterGroup[] = await this.myCostCenterGroupResource.getAllCostCenterGroups().toPromise().catch(() => []);
        this.updateState({ ..._state, costCenterGroups: kostenStellenGruppen });
        const costCenters: ICostCenter[] = await this.myCostCenterResource.getCostCenters(startDate, endDate).toPromise().catch(() => []);
        this.updateState({ ..._state, costCenters });
        this.setIsloading(false);
        this.createKostenstellengruppeDetailModels();
        this.setInputFields();
    }

    public subState(): Observable<ICostCenterState> {
        return this.store$.asObservable();
    }

    public async addKostenstelle(data: ICostCenter): Promise<void> {
        this.setIsloading(true);
        const createdCostCenter: ICostCenter = await this.myCostCenterResource.createCostCenter(data).toPromise().catch(() => null);
        if (createdCostCenter !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-center.actions.added.label');
        }
        this.setIsloading(false);
    }

    public async editKostenstelle(data: ICostCenter): Promise<void> {
        this.setIsloading(true);
        const editedCostCenter: ICostCenter = await this.myCostCenterResource.updateCostCenter(data).toPromise().catch(() => null);
        if (editedCostCenter !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-center.actions.edited.label');
        }
        this.setIsloading(false);
    }

    public async addKostenstellengruppe(data: ICostCenterGroup): Promise<void> {
        this.setIsloading(true);
        const createdGroup: ICostCenterGroup = await this.myCostCenterGroupResource.createCostCenterGroup(data).toPromise().catch(() => null);
        if (createdGroup !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-center-group.actions.added.label');
        }
        this.setIsloading(false);
    }

    public async editKostenstellengruppe(data: ICostCenterGroup): Promise<void> {
        this.setIsloading(true);
        const editedGroup = await this.myCostCenterGroupResource.updateCostCenterGroup(data).toPromise().catch(() => null);
        if (editedGroup !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-center-group.actions.edited.label');
        }
        this.setIsloading(false);
    }

    public setSelectedKostenstelle(costCenter: ICostCenter): void {
        this.updateState({ ..._state, selectedCostCenter: costCenter });
    }

    public async deleteCostCenterGroup(costCenterGroupId: number): Promise<void> {
        this.setIsloading(true);
        try {
            await firstValueFrom(this.myCostCenterGroupResource.deleteCostCenterGroup(costCenterGroupId));
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-center-group.actions.deleted.label');
            await this.fetchData();
        } catch (e) {
            console.error(e);
        }
        this.setIsloading(false);
    }

    public async deleteCostCenter(costCenterId: number): Promise<void> {
        this.setIsloading(true);
        try {
            await firstValueFrom(this.myCostCenterResource.deleteCostCenter(costCenterId));
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-center.actions.deleted.label');
            await this.fetchData();
        } catch (e) {
            console.error(e);
        }
        this.setIsloading(false);
    }

    public async fetchReferenceValues(costAccountingTypeId?: number, referenceId?: number, validFrom?: moment.Moment): Promise<void> {
        this.setIsLoadingReferences(true);
        const state: ICostCenterState = this.getStateSnapshot();
        const costCenterReferenceValues: ICostCenterReferenceValue[] = await this.myCostCenterResource
            .getCostCenterReferenceValuesForCostAccountingType(state.selectedCostCenter.id, costAccountingTypeId, referenceId, validFrom).toPromise().catch(() => []);
        this.updateState({ ..._state, referencesValuesForCostCenter: costCenterReferenceValues });
        this.setIsLoadingReferences(false);
    }

    public async fetchHourlyWages(): Promise<void> {
        this.updateState({ ..._state, isLoadingHourlyWages: true });
        const state: ICostCenterState = this.getStateSnapshot();
        const hourlyWages: IHourlyWage[] = await firstValueFrom(this.myCostCenterResource.getHourlyWages(state.selectedCostCenter.id)).catch(() => []);
        this.updateState({ ..._state, hourlyWages, isLoadingHourlyWages: false });
    }

    public async addHourlyWage(costCenterId: number, wage: IAddHourlyWage): Promise<IHourlyWage> {
        this.updateState({ ..._state, isLoadingHourlyWages: true });
        const addedWage: IHourlyWage = await firstValueFrom(this.myCostCenterResource.addHourlyWage(costCenterId, wage)).catch(() => null);
        if (addedWage !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-center-hourly-wage.actions.added.label');
        }
        this.updateState({ ..._state, isLoadingHourlyWages: false });
        return addedWage;
    }

    public async deleteHourlyWage(costCenterId: number, wageId: number): Promise<void> {
        this.updateState({ ..._state, isLoadingHourlyWages: true });
        try {
            await firstValueFrom(this.myCostCenterResource.deleteHourlyWage(costCenterId, wageId));
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-center-hourly-wage.actions.deleted.label');
        } catch (e) {
            console.error(e);
        }
        this.updateState({ ..._state, isLoadingHourlyWages: false });
    }

    public async addBezugsgroesseToKostenstelle(referenceValue: ICostCenterReferenceValue): Promise<void> {
        this.setIsLoadingReferences(true);
        const state: ICostCenterState = this.getStateSnapshot();
        referenceValue.costCenterId = state.selectedCostCenter.id;
        const value: ICostCenterReferenceValue = await firstValueFrom(this.myCostCenterResource.addCostCenterReferenceValue(referenceValue)).catch(() => null);
        if (value !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-center-reference-value.actions.added.label');
        }
        this.setIsLoadingReferences(false);
    }

    public async editBezugsgroessenWert(referenceValueId: number, amount: number): Promise<void> {
        this.setIsLoadingReferences(true);
        const state: ICostCenterState = this.getStateSnapshot();
        const updatedValue: ICostCenterReferenceValue = await this.myCostCenterResource
            .updateCostCenterReferenceValues(state.selectedCostCenter.id, referenceValueId, { amount }).toPromise().catch(() => null);
        if (updatedValue !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-center-reference-value.actions.edited.label');
        }
        this.setIsLoadingReferences(false);
    }

    public async deleteBezugsgroessenWert(referenceValue: ICostCenterReferenceValue): Promise<void> {
        this.setIsLoadingReferences(true);
        try {
            await this.myCostCenterResource.deleteReferenceValue(referenceValue).toPromise();
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-center-reference-value.actions.deleted.label');
        } catch (e) {
            console.error(e);
        }
        this.setIsLoadingReferences(false);
    }

    public fetchCostAccountingTypes(): Observable<ICostAccountingType[]> {
        if (_state.costAccountingTypes.length > 0) {
            return of(_state.costAccountingTypes);
        }
        this.setIsloading(true);
        return this.myCostAccountingTypeResource.getAllCostAccountingTypes()
            .pipe(
                tap((costAccountingTypes: ICostAccountingType[]) => {
                    this.updateState({ ..._state, costAccountingTypes });
                    this.createCostAccountingTypeLookup(costAccountingTypes);
                }),
                catchError(() => []),
                finalize(() => this.setIsloading(false)),
            );
    }


    public fetchReferences(): Observable<IReference[]> {
        if (_state.references.length > 0) {
            return of(_state.references);
        }
        this.setIsLoadingReferences(true);
        return this.myBezugsgroessenResource.getAllReferences()
            .pipe(
                tap((references: IReference[]) => {
                    this.updateState({ ..._state, references });
                    this.createReferenceLookup(references);
                }),
                catchError(() => []),
                finalize(() => this.setIsLoadingReferences(false)),
            );
    }

    private updateState(state: ICostCenterState): void {
        this.store$.next(_state = state);
    }

    private setDialogFilterFields(): void {


        const hourlyWageFilter: IField[] = [
            {
                field: 'costAccountingType',
                label: 'app.model.hourly-wage.costAccountingType.label',
                type: FieldType.select,
                withClearButton: true,
                selectData: {
                    displaySearch: true,
                    labelKeys: [ 'name' ],
                    valueKey: 'id',
                    options: this.fetchCostAccountingTypes(),
                },
            },
            {
                field: 'validity',
                withClearButton: true,
                label: 'app.model.hourly-wage.validity.label',
                type: FieldType.daterange,
            },
        ];

        const hourlyWageAddFields: IField[] = [
            {
                field: 'costAccountingTypeId',
                label: 'app.model.hourly-wage.costAccountingType.label',
                type: FieldType.select,
                selectData: {
                    displaySearch: true,
                    labelKeys: [ 'name' ],
                    valueKey: 'id',
                    options: this.fetchCostAccountingTypes(),
                },
            },
            {
                field: 'validity',
                label: 'app.model.hourly-wage.validity.label',
                type: FieldType.daterange,
            },
            {
                field: 'hourlyWageEuro',
                label: 'app.model.hourly-wage.wage.label',
                type: FieldType.decimal,
            },
        ];


        this.updateState({ ..._state, hourlyWageFilter, hourlyWageAddFields });
    }

    private setIsloading(isLoading: boolean): void {
        this.updateState({ ..._state, isLoading });
    }

    private setIsLoadingReferences(isLoadingBezugsgroessen: boolean): void {
        this.updateState({ ..._state, isLoadingBezugsgroessen });
    }

    private setInputFields(): void {
        const costCenterGroups: ICostCenterGroup[] = this.getStateSnapshot().costCenterGroups;

        const costCenterGroupSelectData: ISelectData<ICostCenterGroup> = {
            displaySearch: true,
            labelKeys: [ 'name' ],
            valueKey: 'id',
            options: of(costCenterGroups),
        };

        const kostenstelleEditFields = [
            {
                field: 'costCenterGroupId',
                label: 'app.model.cost-center.kostenstgrp_nr.label',
                type: FieldType.select,
                required: true,
                selectData: costCenterGroupSelectData,
            },
            { field: 'name', label: 'app.model.cost-center.kostenstelle_bez.label', type: FieldType.text, required: true },
            { field: 'validFrom', label: 'app.model.cost-center.valid-from.label', type: FieldType.date, required: false },
            { field: 'validTo', label: 'app.model.cost-center.valid-to.label', type: FieldType.date, required: false },
            { field: 'shortName', label: 'app.model.cost-center.short-name.label', type: FieldType.text, required: false },
            {
                field: 'financialAccountingNr',
                label: 'app.model.cost-center.financial-accounting-nr.label',
                type: FieldType.text,
                required: true,
            },
        ];

        const kostenstellenGruppeEditFields = [
            { field: 'name', label: 'app.model.cost-center-group.kostenstgrp_bez.label', type: FieldType.text, required: true },
            {
                field: 'processOrder',
                label: 'app.model.cost-center-group.process-order.label',
                type: FieldType.number,
                required: true,
            },
        ];

        const kostenstelleDaterangeField: IField[] = [ {
            field: 'validDaterange',
            label: 'app.model.cost-center.validity.label',
            type: FieldType.daterange,
        } ];

        this.updateState({ ..._state, costCenterDaterangeField: kostenstelleDaterangeField });
        this.updateState({ ..._state, costCenterDialogFields: kostenstelleEditFields });
        this.updateState({ ..._state, costCenterGroupDialogFields: kostenstellenGruppeEditFields });
    }

    private createKostenstellengruppeDetailModels(): void {
        const state: ICostCenterState = this.getStateSnapshot();

        const map: Record<string, ICostCenter[]> = {};

        // Kostenstellen ohne Gruppe
        const orphanedCostCenters: ICostCenter[] = [];

        // Kostenstellen den Kostenstellengruppen zuweisen
        for (const kostenstelle of state.costCenters) {
            if (kostenstelle.costCenterGroupId === null) {
                orphanedCostCenters.push(kostenstelle);
            } else {
                if (!map[ kostenstelle.costCenterGroupId ]) {
                    map[ kostenstelle.costCenterGroupId ] = [];
                }
                map[ kostenstelle.costCenterGroupId ].push(kostenstelle);
            }
        }

        const kostenstellenGruppenDetail: ICostCenterGroupDetail[] = [];
        for (const kostenstellengruppe of state.costCenterGroups) {
            // Kostenstellengruppe ohne zugewiesene Kostenstellen
            if (!map[ kostenstellengruppe.id ]) {
                map[ kostenstellengruppe.id ] = [];
            }
            kostenstellenGruppenDetail.push({
                id: kostenstellengruppe.id,
                name: kostenstellengruppe.name,
                processOrder: kostenstellengruppe.processOrder,
                costCenters: map[ kostenstellengruppe.id ],
            });
        }
        if (orphanedCostCenters.length !== 0) {
            kostenstellenGruppenDetail.unshift({
                id: 0,
                name: '',
                processOrder: null,
                costCenters: orphanedCostCenters,
            });
        }
        this.updateState({ ..._state, costCenterGroupDetail: kostenstellenGruppenDetail });
    }

    private createReferenceLookup(references: IReference[]): void {
        const referenceLookup: Record<number, IReference> = {};
        for (const reference of references) {
            referenceLookup[ reference.id ] = reference;
        }
        this.updateState({ ..._state, referenceLookup });
    }

    private createCostAccountingTypeLookup(costAccountingTypes: ICostAccountingType[]): void {
        const costAccountingTypeLookup:  Record<string, ICostAccountingType> = {};
        for (const costAccountingType of costAccountingTypes) {
            costAccountingTypeLookup[ costAccountingType.id ] = costAccountingType;
        }
        this.updateState({ ..._state, costAccountingTypeLookup });
    }
}

